<template>
  <v-card class="series-overview-card">
    <div class="series-overview-body">
      <div class="custom-grid">
        <div class="content-section">
          <div class="report-title-section">
            <router-link
              :to="{ name: 'report-overview', params: { id: report.slug } }"
              class="text-h6 font-weight-bold mb-4 text-secondary report-title text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ report.title }}
            </router-link>
          </div>
          <div class="info-grid">
            <div class="info-column">
              <div
                v-for="(gateway, index) in report.gateways"
                class="gateway-status overview-info-block"
                :key="gateway.slug"
                :class="
                  index === (report.gateways || []).length - 1 ? 'mb-0' : 'mb-5'
                "
                v-if="report.gateways && report.gateways.length > 0"
              >
                <div class="text-caption mb-1">
                  {{ gateway.label || 'Not Claimed' }}
                </div>
                <div class="d-flex flex-row align-center">
                  <StatusIcon
                    :status="
                      mapGatewayStatusToIconStatus(gateway.review_status?.slug)
                    "
                  />
                  <div class="text-subcaption">
                    {{
                      formatGatewayStatusTitle(gateway.review_status?.title) ||
                      'Not Claimed'
                    }}
                  </div>
                </div>
              </div>
            </div>
            <div class="info-column">
              <div
                class="overview-info-block mb-6"
                v-for="(item, index) in columnInfoBlocks[1]"
                :key="index"
              >
                <div class="text-caption mb-1">{{ item.label }}</div>
                <div class="text-subcaption">{{ item.value }}</div>
              </div>
            </div>
            <div class="info-column">
              <div
                class="overview-info-block mb-6"
                v-for="(item, index) in columnInfoBlocks[2]"
                :key="index"
              >
                <div class="text-caption mb-1">{{ item.label }}</div>
                <div class="text-subcaption">{{ item.value }}</div>
              </div>
            </div>
            <div class="info-column">
              <div
                class="overview-info-block mb-6"
                v-for="(item, index) in columnInfoBlocks[3]"
                :key="index"
              >
                <div class="text-caption mb-1">{{ item.label }}</div>
                <div class="text-subcaption">{{ item.value }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="image-section">
          <div class="heading-card-image">
            <img
              :src="coverImage"
              :alt="`Cover for ${report.title}`"
              class="report-cover"
              width="232"
              height="300"
              @error="handleImageError"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="series-overview-footer">
      <div class="footer-grid">
        <template v-for="index in 5" :key="index">
          <div
            class="footer-item"
            :class="{ 'has-content': index <= footerItems.length }"
          >
            <div
              v-if="index <= footerItems.length"
              class="overview-info-block"
              :class="{
                'text-no-wrap': footerItems[index - 1].type !== 'format',
              }"
            >
              <template v-if="footerItems[index - 1].type === 'format'">
                <div class="metric-wrapper d-flex flex-row">
                  <div class="d-flex flex-column mr-3">
                    <v-img
                      v-if="footerItems[index - 1].iconSrc"
                      class="format-icon"
                      :width="24"
                      :height="24"
                      :src="footerItems[index - 1].iconSrc"
                      alt="Format Icon"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="d-flex flex-column">
                    <div class="text-caption">
                      {{ footerItems[index - 1].label }}
                    </div>
                    <div class="text-subcaption">
                      {{ footerItems[index - 1].value }}
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="text-caption">
                  {{ footerItems[index - 1].label }}
                </div>
                <div class="text-subcaption">
                  {{ footerItems[index - 1].value }}
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import type { Report, GradeInfo } from '../../store';
  import { formatGradeDisplay } from '@/lib/grades';
  import { formatDisplayValue, formatDisplayFormat } from '@/lib/formatUtils';
  import { useReportsStore } from '../../store';
  import StatusIcon from '../common/StatusIcon.vue';

  interface FooterItem {
    label: string;
    value: string;
    type: string;
    iconSrc?: string;
  }

  const props = defineProps<{
    report: Report;
  }>();

  const reportsStore = useReportsStore();

  const fallbackCoverUrl =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMzIgMzAwIj48cmVjdCB3aWR0aD0iMjMyIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2ViZjdmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiMwMDdlYWIiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPlJlcG9ydCBDb3ZlcjwvdGV4dD48L3N2Zz4=';

  const formattedReleaseDate = computed(() => {
    if (!props.report.release) return 'Not Claimed';
    try {
      return new Date(props.report.release).toLocaleDateString();
    } catch (e) {
      return 'Not Claimed';
    }
  });

  function handleImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = fallbackCoverUrl;
  }

  function getRawScoreAggregation(criterionName: string): string | null {
    if (!props.report.gateway_overviews) {
      return null;
    }

    let totalScore = 0;
    let totalPossible = 0;
    let foundData = false;

    // Search through all gateway overviews for the criterion
    props.report.gateway_overviews.forEach((gateway) => {
      if (gateway.criterion_overviews) {
        gateway.criterion_overviews.forEach((criterion) => {
          // Clean HTML tags and match criterion name
          const criterionLabel = (
            criterion.name ||
            criterion.label ||
            ''
          ).replace(/<[^>]*>/g, '');
          if (criterionLabel === criterionName && criterion.indicators) {
            criterion.indicators.forEach((indicator) => {
              if (indicator.rating) {
                const score = indicator.rating.score || 0;
                const total = indicator.rating.total || 0;
                const reviewStatus = indicator.rating.review_status?.slug || '';
                const isValidScore =
                  total > 0 &&
                  !reviewStatus.includes('did-not-review') &&
                  !reviewStatus.includes('not-eligible') &&
                  !reviewStatus.includes('not-claimed');
                if (isValidScore) {
                  totalScore += score;
                  totalPossible += total;
                  foundData = true;
                }
              }
            });
          }
        });
      }
    });

    if (foundData && totalPossible > 0) {
      const percentage = Math.round((totalScore / totalPossible) * 100);
      return `${totalScore}/${totalPossible} (${percentage}%)`;
    }
    return 'Not Claimed';
  }

  function getDetailValue(name: string): string {
    // First check in the details array
    const detail = props.report.details?.find(
      (d) => d.name?.replace(/<[^>]*>/g, '') === name,
    );
    if (detail?.value && detail.value !== 'Not Claimed') {
      return detail.value;
    }

    // For Foundational Skills, aggregate (average) available percentages from criteria_groups only
    if (name === 'Foundational Skills') {
      if (props.report.overviews && props.report.overviews.length > 0) {
        for (const overview of props.report.overviews) {
          if (overview.criteria_groups) {
            for (const group of overview.criteria_groups) {
              if (group.criteria) {
                const criterion = group.criteria.find(
                  (c) => c.label?.replace(/<[^>]*>/g, '') === name,
                );
                if (
                  criterion &&
                  criterion.scores &&
                  criterion.scores.length > 0
                ) {
                  // Find all valid percentage scores
                  const validPercentages = criterion.scores
                    .map((score) => {
                      let reviewStatus = '';
                      if (typeof score.review_status === 'string') {
                        reviewStatus = score.review_status;
                      } else if (
                        score.review_status &&
                        typeof score.review_status === 'object' &&
                        'slug' in score.review_status
                      ) {
                        reviewStatus = score.review_status.slug || '';
                      }
                      if (
                        score.score &&
                        score.score !== '0%' &&
                        score.score !== '0&#37;' &&
                        reviewStatus !== 'not-claimed' &&
                        reviewStatus !== 'did-not-review' &&
                        reviewStatus !== 'not-eligible'
                      ) {
                        return parseInt(
                          score.score.replace('&#37;', '').replace('%', ''),
                          10,
                        );
                      }
                      return null;
                    })
                    .filter((val) => val !== null) as number[];

                  if (validPercentages.length > 0) {
                    const avg = Math.round(
                      validPercentages.reduce((a, b) => a + b, 0) /
                        validPercentages.length,
                    );
                    return `${avg}%`;
                  }
                }
              }
            }
          }
        }
      }
      return 'Not Claimed';
    }

    // If not found in details, check in overviews criteria groups
    if (props.report.overviews && props.report.overviews.length > 0) {
      const overview = props.report.overviews[0];
      if (overview.criteria_groups) {
        for (const group of overview.criteria_groups) {
          if (group.criteria) {
            const criterion = group.criteria.find(
              (c) => c.label?.replace(/<[^>]*>/g, '') === name,
            );
            if (criterion && criterion.scores && criterion.scores.length > 0) {
              // Check for not-claimed entries
              const hasNotClaimedEntries = criterion.scores.some((score) => {
                const reviewStatus =
                  typeof score.review_status === 'string'
                    ? score.review_status
                    : score.review_status?.slug || '';
                const statusLower = String(reviewStatus || '').toLowerCase();
                const isNotClaimedFromData =
                  statusLower.includes('not-claimed') ||
                  statusLower.includes('not_claimed') ||
                  statusLower === 'did-not-review' ||
                  statusLower === 'did_not_review';
                const isZeroScoreWithEmptyStatus =
                  (score.score === '0%' || score.score === '0&#37;') &&
                  score.review_status &&
                  typeof score.review_status === 'object' &&
                  (!score.review_status.slug ||
                    score.review_status.slug === '') &&
                  (!score.review_status.name ||
                    score.review_status.name === '') &&
                  (!score.review_status.title ||
                    score.review_status.title === '');
                const isNotClaimed =
                  isNotClaimedFromData || isZeroScoreWithEmptyStatus;
                return isNotClaimed;
              });
              if (hasNotClaimedEntries) {
                return 'Not Claimed';
              }
              // For other criteria, use the highest percentage score (original logic)
              const validScores = criterion.scores.filter((score) => {
                const reviewStatus =
                  typeof score.review_status === 'string'
                    ? score.review_status
                    : score.review_status?.slug || '';
                const statusLower = String(reviewStatus || '').toLowerCase();
                return (
                  score.score &&
                  score.score !== '0%' &&
                  score.score !== '0&#37;' &&
                  !statusLower.includes('not-claimed') &&
                  !statusLower.includes('not_claimed') &&
                  !statusLower.includes('not-eligible') &&
                  !statusLower.includes('not_eligible') &&
                  statusLower !== 'did-not-review' &&
                  statusLower !== 'did_not_review'
                );
              });
              if (validScores.length > 0) {
                const percentageScores = validScores
                  .map((score) => {
                    const scoreValue = score.score?.replace('&#37;', '%') || '';
                    const match = scoreValue.match(/(\d+)%/);
                    return match ? parseInt(match[1], 10) : 0;
                  })
                  .filter((val) => val > 0);
                if (percentageScores.length > 0) {
                  const highestScore = Math.max(...percentageScores);
                  return `${highestScore}%`;
                }
                return 'Scores Available';
              }
              const allNotClaimed = criterion.scores.every((score) => {
                const reviewStatus =
                  typeof score.review_status === 'string'
                    ? score.review_status
                    : score.review_status?.slug || '';
                const statusLower = String(reviewStatus || '').toLowerCase();
                const isExplicitlyNotClaimed =
                  statusLower.includes('not-claimed') ||
                  statusLower.includes('not_claimed') ||
                  statusLower === 'did-not-review' ||
                  statusLower === 'did_not_review';
                const isZeroWithEmptyStatus =
                  (score.score === '0%' || score.score === '0&#37;') &&
                  (!reviewStatus || reviewStatus === '');
                const isNotClaimed =
                  isExplicitlyNotClaimed || isZeroWithEmptyStatus;
                return isNotClaimed;
              });
              if (allNotClaimed) {
                return 'Not Claimed';
              }
              const hasNotClaimedScores = criterion.scores.some((score) => {
                const reviewStatus =
                  typeof score.review_status === 'string'
                    ? score.review_status
                    : score.review_status?.slug || '';
                const statusLower = String(reviewStatus || '').toLowerCase();
                return (
                  statusLower.includes('not-claimed') ||
                  statusLower.includes('not_claimed')
                );
              });
              return 'Not Claimed';
            }
          }
        }
      }
    }
    return 'Not Claimed';
  }

  const coverImage = computed(() => {
    return props.report.cover || fallbackCoverUrl;
  });

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Helper function to format copyright by removing dashes between year and publisher
  const formatCopyright = (copyright: string): string => {
    if (!copyright) return '';

    let result = copyright;

    // Remove any dash-like character between year and publisher
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*[–—\-]\s*(?=\D)/g, '$1 ');

    // Handle HTML entities for dashes
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&#8211;\s*/g, '$1 ');
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&#8212;\s*/g, '$1 ');
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&ndash;\s*/g, '$1 ');
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&mdash;\s*/g, '$1 ');

    // Clean up multiple spaces and trim
    result = result.replace(/\s+/g, ' ').trim();

    // Decode any remaining HTML entities
    return decodeHtmlEntities(result);
  };

  const formattedPublisherWithCopyright = computed(() => {
    const publisher = props.report.publisher || '';
    const copyright = props.report.copyright
      ? formatCopyright(props.report.copyright)
      : '';

    // Helper function to extract the core publisher name for comparison
    const getCorePublisherName = (pubName: string): string => {
      // Handle Savvas/Pearson special case - extract the main name before any parenthetical
      const match = pubName.match(/^([^(]+?)(?:\s*\([^)]*\))?$/);
      return match ? match[1].trim() : pubName.trim();
    };

    // If copyright already contains the publisher name (or core publisher name), just use the copyright
    if (copyright && publisher) {
      const corePublisher = getCorePublisherName(publisher);
      if (copyright.includes(publisher) || copyright.includes(corePublisher)) {
        return copyright;
      }
    }

    if (publisher && copyright) {
      return `${publisher} ${copyright}`;
    } else if (publisher) {
      return publisher;
    } else if (copyright) {
      return copyright;
    }
    return 'Not Claimed';
  });

  const columnInfoBlocks = computed(() => {
    return [
      [],
      [
        {
          label: 'GRADES',
          value: formatGradeDisplay(
            props.report.grades as string[] | string | null,
          ),
        },
        {
          label: 'Report Released',
          value: formattedReleaseDate.value,
        },
      ].filter((item) => item.value && item.value !== 'Not Claimed'),
      [
        {
          label: 'PUBLISHER',
          value: formattedPublisherWithCopyright.value,
        },
        {
          label: 'Review Tool',
          value: props.report.versions?.[0] || 'Not Claimed',
        },
      ].filter((item) => item.value && item.value !== 'Not Claimed'),
      [],
    ];
  });

  const formatIconMap: Record<string, string> = {
    'core-comprehensive': new URL('@/assets/icon-format-3.svg', import.meta.url)
      .href,
    'core comprehensive': new URL('@/assets/icon-format-3.svg', import.meta.url)
      .href,
    'core: comprehensive': new URL(
      '@/assets/icon-format-3.svg',
      import.meta.url,
    ).href,
    'core-no-foundational-skills': new URL(
      '@/assets/icon-format-2.svg',
      import.meta.url,
    ).href,
    'core no foundational skills': new URL(
      '@/assets/icon-format-2.svg',
      import.meta.url,
    ).href,
    'core: no foundational skills': new URL(
      '@/assets/icon-format-2.svg',
      import.meta.url,
    ).href,
    'supplemental-foundational-skills-only': new URL(
      '@/assets/icon-format-1.svg',
      import.meta.url,
    ).href,
    'supplemental foundational skills only': new URL(
      '@/assets/icon-format-1.svg',
      import.meta.url,
    ).href,
    'supplemental: foundational skills only': new URL(
      '@/assets/icon-format-1.svg',
      import.meta.url,
    ).href,
    'supplemental-foundational-skills': new URL(
      '@/assets/icon-format-1.svg',
      import.meta.url,
    ).href,
    'supplemental foundational skills': new URL(
      '@/assets/icon-format-1.svg',
      import.meta.url,
    ).href,
    'supplemental: foundational skills': new URL(
      '@/assets/icon-format-1.svg',
      import.meta.url,
    ).href,
  };

  // Helper function to get the correct format icon
  const getFormatIcon = (formatValue: string | undefined): string | null => {
    if (!formatValue) return null;

    const originalKey = formatValue.toLowerCase();
    const normalizedKey = formatValue.toLowerCase().replace(/-/g, ' ');
    const transformedKey = formatDisplayFormat(formatValue).toLowerCase();

    // Try original format first, then normalized, then transformed format
    return (
      formatIconMap[originalKey] ||
      formatIconMap[normalizedKey] ||
      formatIconMap[transformedKey] ||
      null
    );
  };

  // Helper function to determine if grades are K-5 vs 6-12
  const isElementaryGrades = (grades: string | string[] | null): boolean => {
    if (!grades) return false;

    // Handle array of grades (from report_grades)
    if (Array.isArray(grades)) {
      return grades.some((grade) =>
        ['K', '1', '2', '3', '4', '5'].includes(grade),
      );
    }

    // Handle string grade range (from grades field)
    if (typeof grades === 'string') {
      const gradeStr = grades.toLowerCase();
      // Check for K-5 patterns
      if (
        gradeStr.includes('k-5') ||
        gradeStr.includes('k-8') ||
        gradeStr.includes('k-2') ||
        gradeStr.includes('3-5')
      ) {
        return true;
      }
      // Check for 6-12 patterns
      if (
        gradeStr.match(/[6-9]/) ||
        gradeStr.includes('6-8') ||
        gradeStr.includes('6-12') ||
        gradeStr.includes('9-12')
      ) {
        return false;
      }
      // Check individual grades
      return (
        gradeStr.includes('k') ||
        ['1', '2', '3', '4', '5'].some((g) => gradeStr.includes(g))
      );
    }

    return false;
  };

  const footerItems = computed<FooterItem[]>(() => {
    const subjectValue =
      typeof props.report.subject === 'string'
        ? props.report.subject
        : props.report.subject?.name || props.report.subject?.title;
    const formatValue =
      Array.isArray(props.report.formats) &&
      typeof props.report.formats[0] === 'string'
        ? props.report.formats[0]
        : undefined;

    // Get subject slug for conditional logic
    const subjectSlug =
      typeof props.report.subject === 'string'
        ? props.report.subject
        : props.report.subject?.slug || '';

    // Always show these 2 items first
    const items: FooterItem[] = [
      {
        label: 'Subject',
        value: reportsStore.formatSubjectName(subjectValue)
          ? reportsStore.formatSubjectName(subjectValue)!
          : 'Not Claimed',
        type: 'text',
      },
      {
        label: 'Format',
        value: formatDisplayFormat(formatValue) || 'Not Claimed',
        type: 'format',
        iconSrc: getFormatIcon(formatValue) || undefined,
      },
    ];

    // Add remaining items from details array
    if (props.report.details && props.report.details.length > 0) {
      // Add up to 3 details as footer items
      props.report.details.slice(0, 3).forEach((detail) => {
        if (detail.name && detail.value) {
          const transformedValue =
            detail.value === 'available'
              ? 'Scores Available'
              : decodeHtmlEntities(detail.value);
          items.push({
            label: detail.name.replace(/<[^>]*>/g, ''),
            value: transformedValue,
            type: 'text',
          });
        }
      });
    }

    return items;
  });

  function mapGatewayStatusToIconStatus(
    statusSlug: string | undefined,
  ): string {
    if (!statusSlug) return 'gray';
    const statusMap: Record<string, string> = {
      meets: 'green',
      'partially-meets': 'yellow',
      'does-not-meet': 'red',
      'not-eligible': 'gray',
      'does-not-exist': 'gray',
      'no-status': 'gray',
      'narrative-only': 'gray',
      'zero-score': 'red',
    };
    return statusMap[statusSlug] || 'gray';
  }

  function formatGatewayStatusTitle(
    title: string | undefined,
  ): string | undefined {
    if (title === 'Does Not Exist') {
      return 'Not Eligible';
    }
    return title;
  }
</script>

<style lang="scss" scoped>
  @use 'vuetify/settings' as settings;
  @use 'sass:map';

  .series-overview-card {
    .overview-info-block {
      .text-caption {
        text-transform: uppercase !important;
      }
      .text-subcaption {
        font-size: 16px;
        font-weight: bold;
        color: rgb(var(--v-theme-secondary));
        &.alt {
          font-size: 12px;
          font-weight: normal;
        }
      }
      &.gateway-status {
        .text-subcaption {
          font-size: 14px;
        }
      }
    }

    .series-overview-body {
      padding: 32px;

      .report-title {
        font-family: 'rockwell', sans-serif;
        font-size: 48px !important;
        font-weight: normal !important;
      }
    }
  }

  /* Custom Grid Layout */
  .custom-grid {
    display: flex;
    gap: 24px;
    align-items: stretch;
    min-height: 300px;
  }

  .content-section {
    flex: 1;
    min-width: 0; /* Allows flex item to shrink */
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-height: 280px;
  }

  .report-title-section {
    flex-shrink: 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-top: auto;
    padding-top: 2rem;
    flex-shrink: 0;
  }

  .info-column {
    min-width: 0; /* Prevents overflow */
  }

  .image-section {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  /* Footer Grid Layout */
  .footer-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr); /* Always 5 equal columns */
    gap: 0;
  }

  .footer-item {
    border-right: 1px solid rgba(var(--v-theme-blue), 0.2);
    margin: 6px 0;
    min-height: 60px; /* Ensure consistent height even when empty */

    &:not(.has-content) {
      border-right: none; /* Remove border for empty items */
    }

    .overview-info-block {
      width: 100%;
      padding: 6px 12px;
    }

    &:last-child {
      border-right: none;
    }
  }

  .series-overview-card {
    .series-overview-footer {
      background-color: #f0f3f5;

      .metric-wrapper {
        .text-caption {
          text-transform: uppercase !important;
        }
        .text-subcaption {
          line-height: 1.2;
          word-wrap: break-word;
          hyphens: auto;
        }
      }
    }
  }

  /* Responsive Design */
  @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
    .custom-grid {
      flex-direction: column;
      gap: 16px;
    }

    .image-section {
      order: -1; /* Show image first on mobile */
      justify-content: center;
    }

    .info-grid {
      grid-template-columns: 1fr; /* Single column on mobile */
    }

    .overview-info-block {
      margin-bottom: 1.5rem !important;
    }

    .footer-grid {
      grid-template-columns: 1fr; /* Single column on mobile */
      gap: 0;
    }

    .footer-item:empty {
      display: none; /* Hide empty items on mobile */
    }

    .footer-item {
      border-right: none;
      border-bottom: 1px solid rgba(var(--v-theme-blue), 0.25);
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;

      .overview-info-block {
        padding: 16px 12px;
        padding-bottom: 0.75rem;
      }

      &:last-child {
        border-bottom: none;

        .overview-info-block {
          padding-bottom: 16px;
        }
      }
    }
  }

  .heading-card-image {
    border: 1px solid var(--card-border-grey);
    display: block;
    .report-cover {
      display: block;
      width: 232px;
      height: 300px;
      max-width: 232px;
    }
  }

  /* Print styles */
  @media print {
    /* Hide card styling in print */
    .series-overview-card {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }

    /* Force print layout */
    .series-overview-body {
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      display: block !important;
    }

    /* NUCLEAR OPTION: Force everything to be block layout */
    .custom-grid {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    /* Force image section to be on its own line - 100% width */
    .image-section {
      display: block !important;
      width: 100% !important;
      flex: 0 0 100% !important;
      max-width: 100% !important;
      text-align: center !important;
      margin: 0 auto 20px auto !important;
      padding: 0 !important;
      page-break-inside: avoid !important;
    }

    /* Center everything about the image */
    .heading-card-image {
      border: none !important;
      display: block !important;
      width: 200px !important;
      max-width: 200px !important;
      margin: 0 auto !important;
      text-align: center !important;
    }

    /* Force image to be centered */
    .report-cover {
      width: 180px !important;
      max-width: 180px !important;
      height: auto !important;
      margin: 0 auto !important;
      display: block !important;
      position: static !important;
      left: auto !important;
      right: auto !important;
      transform: none !important;
      float: none !important;
    }

    /* Content section comes AFTER image on new line */
    .content-section {
      display: block !important;
      width: 100% !important;
      clear: both !important;
      margin: 20px 0 0 0 !important;
      padding: 0 !important;
    }

    /* Simplify content for print */
    .report-title-section {
      margin-bottom: 16px !important;
    }

    .info-grid {
      display: block !important;
      width: 100% !important;
    }

    .info-column {
      display: block !important;
      width: 100% !important;
      margin-bottom: 8px !important;
      page-break-inside: avoid !important;
    }
  }
</style>
